import React, { useContext } from 'react';

import { fileUploadApis } from '@apis/next/fileUploadApis';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import { StatusFlowsContext } from '@components/admin/booking/BookingStatusFlows';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContent,
  ActionableDialogTitle,
} from '@components/Dialog';
import { FileInput } from '@components/NextForms/FileInput';
import { useFormUtils } from '@hooks/useFormUtils';
import { FileTypeHelper, RemoteFile } from '@models/next/FileType';
import { MoveOutInfoForm } from '@models/next/StatusFlows';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';

const strings = createStrings({
  jp: {
    moveOutProcessCompleted: '搬出手配済',
    update: '書類変更',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialogContent: {
    ...common.textGray700,
    ...common.middleText,
    padding: '1.5rem 1.5rem',
  },
  field: {
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1fr 2fr',
  },
  button: {
    width: '132px',
  },
}));

export interface MoveOutUpdateDialogOptions {
  open: boolean;
  edit: boolean;
  moveOutFile?: RemoteFile | null;
  onClose: () => void;
}

export const MoveOutUpdateDialog: React.FC<MoveOutUpdateDialogOptions> = (props) => {
  const { open, edit, onClose, moveOutFile } = props;
  const classes = useStyles();
  const { d2dNo, invalidateStatusFlows } = useContext(StatusFlowsContext);

  const postFileUpload = fileUploadApis.usePost('/file/upload');
  const patchPreAlert = dashboardPreAlertApis.usePatch(
    '/dashboard/pre-alert/:d2d_no',
    {
      params: { d2d_no: d2dNo },
    },
    { onSuccess: () => invalidateStatusFlows() },
  );

  const form = useForm(MoveOutInfoForm, {
    initialOutput: {
      move_out_no_file: moveOutFile ? FileTypeHelper.remoteToLocal(moveOutFile) : empty,
    },
  });
  const { fields } = form.root;
  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [postFileUpload, patchPreAlert],
  });
  return (
    <ActionableDialog open={open} onClose={onClose}>
      <ActionableDialogTitle>
        {strings.moveOutProcessCompleted}
        {edit && `-${strings.update}`}
      </ActionableDialogTitle>
      {renderAlertsAndSpinnerModal()}
      <form
        {...form.bindForm({
          async onSubmit(data) {
            const [moveOutFile] = data.move_out_no_file.remote
              ? [FileTypeHelper.localToRemote(data.move_out_no_file)]
              : await postFileUpload.mutateAsync({
                  file: [data.move_out_no_file.file],
                });
            await patchPreAlert.mutateAsync({
              move_out_no_file: moveOutFile,
              move_out_status_name: edit ? undefined : '搬出手配済',
            });

            onClose();
          },
          onSubmitError(errors) {
            console.error(JSON.stringify(form.rawInput));
            console.error(errors);
          },
        })}
      >
        <ActionableDialogContent className={classes.dialogContent}>
          <div className={classes.field}>
            <label>{fields.move_out_no_file.formMeta.label}</label>
            <div>
              <FileInput
                field={fields.move_out_no_file}
                hideButtonIfHasFile
                accept={'application/pdf'}
                buttonProps={{
                  shape: 'round',
                  className: classes.button,
                }}
              />
            </div>
          </div>
        </ActionableDialogContent>
        <ActionableDialogActions>
          <Button
            color="grey"
            text={formStrings.cancel}
            size="small"
            type="button"
            onClick={() => onClose()}
          />
          <Button
            color="primary"
            text={edit ? formStrings.ok : strings.moveOutProcessCompleted}
            size="small"
            type="submit"
          />
        </ActionableDialogActions>
      </form>
    </ActionableDialog>
  );
};
